import type { ReactNode } from "react";
import { CaretRight, ShieldCheck } from "@phosphor-icons/react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import { useServices } from "../data/requestQueries";
import {
  composeAccountMap,
  useClientAccountMap,
  useTenantContacts,
  type AccountMapEntry,
  type ClientAccessRecord
} from "../data/accountMapQueries";
import { Badge, Quiet } from "../ui/Badge";
import { BoxState } from "../ui/BoxState";
import { ClientHead } from "../ui/ClientHead";
import { Hint } from "../ui/Hint";
import { formatLongDay, formatShortDay, formatWhen } from "./clientOps/format";
import "./clientOps/clientOps.css";

/**
 * Konton, the client half of plan.md section 5.9.
 *
 * The register exists because ownership alone does not answer which workspace, login, role or
 * recovery path applies during an incident or an offboarding. plan.md section 2 makes that one of
 * the platform's four jobs: "Make client ownership and clean offboarding concrete. The client owns
 * its production accounts and Petrovic holds personal, revocable access where access is needed."
 *
 * **This screen answers one question and is laid out around it: if Petrovic disappeared tomorrow,
 * could you keep running.** That is why the question rides on the heading, why the recovery owner is
 * a fact on every account rather than an optional column, and why an account nobody is named to
 * recover carries a badge instead of a blank. It is deliberately not the shape of `/admin/clients`,
 * which asks the operator's question, "what is the state of this register", and answers it with a
 * dense operations console. The same rows, a different question, a different screen.
 *
 * Drawn after `prototype/kund/pages.js` `accountsPage` in the second build round of 2026-09-25: one
 * box per account, the facts as a kv list, what is in order as a grey word and what is missing as a
 * badge (design.md section 3, rule 2), and the accesses as rows that open in place to their dates
 * and their history. The two introduction paragraphs went (rule 3): the question is the heading's
 * Hint, and the exclusion below is one line in a box of its own.
 *
 * ---------------------------------------------------------------------------
 * What is not on this screen
 * ---------------------------------------------------------------------------
 *
 * Petrovic's own access rows are not here, and they are not here twice over: the RLS policies
 * `account_access_records_select_member` and `account_access_events_select_member` both require
 * `person_kind <> 'petrovic'`, and `public.v_client_account_map` excludes the same rows in its own
 * text for every caller, including one no policy filters. schema.md section 7 sets that out and
 * `tests/db/access-map-read-model.test.ts` proves the second lock on its own. Nothing rendered
 * below may reintroduce a Petrovic identity, and nothing below tries to infer one.
 *
 * The reason schema.md gives is what the rows are worth to the reader: "Petrovic's own access rows
 * give the client nothing it can act on, and they displace the ones that do: a third party still
 * holding access, an access that was revoked, and an account with no recovery owner."
 *
 * **The consequence is said out loud rather than left to be inferred.** An account whose only
 * access row is ours arrives here with an empty list, and an unlabelled empty list under a heading
 * called Behörigheter reads as "nobody can reach this account". That is a different claim and a
 * false one. So the page carries one standing line naming the exclusion, and every empty access
 * list repeats its point. The line is about the screen and never about a row, because a per-account
 * marker would say which accounts Petrovic can reach, which is precisely the inference the two locks
 * exist to refuse.
 *
 * ---------------------------------------------------------------------------
 * Closed accounts stay
 * ---------------------------------------------------------------------------
 *
 * `active` is projected by the view and deliberately not filtered, and it is not filtered here
 * either. schema.md section 7: filtering the row "would take a closed account's revocation history
 * out of the client's read at the moment they have a reason to look for it". A closed account is
 * listed under the open ones, marked as closed, with its history intact.
 */

/**
 * The three values `account_references_ownership_status_check` allows, in the client's own reading.
 *
 * `/admin/clients` renders the same column as "Kunden äger kontot". Here the reader is the client, so
 * the subject changes and the sentence says what it means for them. `petrovic_held_pending_transfer`
 * is the one that must not be softened: an account still in Petrovic's name is the exact state
 * plan.md section 2 job 4 exists to eliminate, and the client is entitled to see that it has not
 * been eliminated yet.
 */
const OWNERSHIP_STATUS: Record<string, string | undefined> = {
  client_owned: "Ni äger kontot",
  petrovic_held_pending_transfer: "Vi håller kontot tills det är överfört till er",
  third_party_owned: "En tredje part äger kontot"
};

/**
 * The four values `account_references_mfa_status_check` allows.
 *
 * `unknown` and `not_applicable` stay separate, as they do on the admin side. One is a question
 * nobody has put to the supplier and the other is a supplier with no two-factor to turn on, and
 * only the first is something to go and find out. So since 2026-09-25 the two that ask something
 * carry a badge and the two that do not are grey words (design.md section 3, rule 2).
 */
const MFA_STATUS: Record<string, { label: string; warn: boolean } | undefined> = {
  enabled: { label: "Påslagen", warn: false },
  disabled: { label: "Avstängd", warn: true },
  unknown: { label: "Inte kontrollerad", warn: true },
  not_applicable: { label: "Finns inte hos leverantören", warn: false }
};

/**
 * The three person kinds that reach a client's read. `petrovic` is absent by construction.
 *
 * It is left out of this map rather than mapped to a placeholder, so that a row which somehow
 * carried it would fall through to the unknown branch and print nothing rather than a label.
 */
const PERSON_KIND: Record<string, string | undefined> = {
  client: "Någon hos er",
  successor: "Efterträdare",
  other: "Annan part"
};

/** The four values `account_access_records_access_status_check` allows. */
const ACCESS_STATUS: Record<string, string | undefined> = {
  requested: "Begärd",
  active: "Aktiv",
  missing: "Saknas",
  revoked: "Återkallad"
};

/** The six values `account_access_events_event_type_check` allows, in the past tense a log reads in. */
const EVENT_TYPE: Record<string, string | undefined> = {
  requested: "Behörigheten begärdes",
  granted: "Behörigheten tilldelades",
  role_changed: "Rollen ändrades",
  reviewed: "Behörigheten granskades",
  missing: "Behörigheten saknades",
  revoked: "Behörigheten återkallades"
};

/**
 * A value the database has and this build has no word for.
 *
 * Printed as the raw column value in mono rather than mapped to a neighbouring label. A new
 * `ownership_status` mapped to "Ni äger kontot" would be the screen guessing on the one row where
 * guessing is expensive, and a blank would read as a fact nobody recorded.
 */
function Raw({ value }: { value: string }) {
  return <span className="k-mono">{value}</span>;
}

/**
 * One labelled fact about an account, a row of the kv list.
 *
 * A definition list rather than a table, because the facts are four to six per account and a table
 * of six columns does not survive 375 px without either truncation or a horizontal scroll.
 */
function Fact({ term, testId, children }: { term: string; testId?: string; children: ReactNode }) {
  return (
    <div data-testid={testId}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Moment({ value }: { value: string }) {
  return <time dateTime={value}>{formatWhen(value)}</time>;
}

function AccessRow({ record }: { record: ClientAccessRecord }) {
  const kind = PERSON_KIND[record.person_kind];
  const status = ACCESS_STATUS[record.access_status];
  const reviewed = formatShortDay(record.last_reviewed_at);

  /*
    Aktiv is the normal state and says nothing on the row; the others say what they are, because a
    revoked access that looked like a live one would undo the reason the row is kept. Saknas asks
    the client to act, so it is the one badge.
  */
  const trailStatus =
    record.access_status === "active" ? null : record.access_status === "missing" ? (
      <Badge tone="warn">{status}</Badge>
    ) : (
      <Quiet>{status ?? record.access_status}</Quiet>
    );

  return (
    <li
      className="k-access"
      data-person-kind={record.person_kind}
      data-access-status={record.access_status}
      data-testid="access-row"
    >
      <details className="k-open">
        <summary>
          {/*
            The supplier's own words for the identity and the role, shown exactly as written. They
            are free text and `not null`, and mapping them into a portal vocabulary would claim to
            know what a given supplier means by Administrator.
          */}
          <span className="primary-cell">
            <strong className="k-mono">{record.provider_identity_label}</strong>
            <span>
              <span>{record.provider_role}</span>
              {" · "}
              <span data-testid="access-kind">{kind ?? <Raw value={record.person_kind} />}</span>
            </span>
          </span>
          <span className="k-access-trail">
            {trailStatus}
            {/*
              A review date that is missing is a finding and not an absence to hide: an access nobody
              has looked at since it was granted is the one most likely to still exist at a supplier
              after the person it belonged to has left.
            */}
            <span className="quiet" data-testid="access-reviewed">
              {reviewed && record.last_reviewed_at ? (
                <>
                  Granskad <time dateTime={record.last_reviewed_at}>{reviewed}</time>
                </>
              ) : (
                "Aldrig granskad"
              )}
            </span>
            <span className="k-caret" aria-hidden="true">
              <CaretRight size={16} />
            </span>
          </span>
        </summary>

        <div className="k-access-body">
          <dl className="kv">
            <Fact term="Status">
              {/* design.md section 5: colour is never the only status signal. The word leads. */}
              <span data-testid="access-status">{status ?? <Raw value={record.access_status} />}</span>
            </Fact>
            {record.granted_at && formatWhen(record.granted_at) ? (
              <Fact term="Tilldelad">
                <Moment value={record.granted_at} />
              </Fact>
            ) : null}
            {record.revoked_at && formatWhen(record.revoked_at) ? (
              <Fact term="Återkallad">
                <Moment value={record.revoked_at} />
              </Fact>
            ) : null}
          </dl>

          {record.events.length > 0 ? (
            <ol className="k-timeline" data-testid="access-history">
              {record.events.map((entry) => {
                const label = EVENT_TYPE[entry.event_type];
                const moment = formatWhen(entry.occurred_at);

                return (
                  <li key={entry.id} data-testid="access-event">
                    <span>{label ?? <Raw value={entry.event_type} />}</span>
                    {/*
                      Both roles or neither. A `role_changed` event carries a before and an after; a
                      `granted` event carries an after alone and a `revoked` event carries neither, so
                      each half is asked for separately rather than assumed to travel with the other.
                    */}
                    {entry.provider_role_before || entry.provider_role_after ? (
                      <span>
                        {entry.provider_role_before ?? "ingen roll"} till {entry.provider_role_after ?? "ingen roll"}
                      </span>
                    ) : null}
                    {moment ? <time dateTime={entry.occurred_at}>{moment}</time> : null}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="quiet k-small" data-testid="access-no-history">
              Ingen historik är uppskriven för den här behörigheten.
            </p>
          )}
        </div>
      </details>
    </li>
  );
}

function AccountBox({ entry }: { entry: AccountMapEntry }) {
  const { reference, serviceName, serviceEndedAt, clientOwnerName, recoveryOwnerName, recoveryOwnerAbsent } = entry;
  const access = reference.access;
  const mfa = MFA_STATUS[reference.mfa_status];
  const titleId = `account-${reference.id}`;

  return (
    <section
      className={`panel k-account${reference.active ? "" : " is-closed"}`}
      aria-labelledby={titleId}
      data-active={reference.active ? "true" : "false"}
      data-testid="account-card"
    >
      <header className="panel-head">
        <h2 id={titleId}>{reference.account_label}</h2>
        <span className="quiet k-account-where">
          <span>{reference.provider}</span>
          {serviceName ? (
            <>
              {" · "}
              <span>{serviceName}</span>
              {serviceEndedAt ? (
                <>
                  {" · "}
                  <span data-testid="account-service-ended">Avslutad {formatLongDay(serviceEndedAt)}</span>
                </>
              ) : null}
            </>
          ) : null}
          {/*
            A closed account is marked rather than removed, and the mark is a word. schema.md
            section 7 keeps the row in the client's read precisely so the revocation history under it
            survives, and a row that looked identical to a live one would undo that.
          */}
          {reference.active ? null : (
            <>
              {" · "}
              <span data-testid="account-closed">Avslutat konto</span>
            </>
          )}
        </span>
      </header>

      <div className="panel-body">
        <dl className="kv">
          <Fact term="Ägande" testId="account-ownership">
            {OWNERSHIP_STATUS[reference.ownership_status] ?? <Raw value={reference.ownership_status} />}
          </Fact>

          <Fact term="Ägare hos er" testId="account-owner">
            {/*
              The column is `not null` behind a restricting foreign key, so a missing name means the
              contact list did not reach us and not that the account has no owner. Saying which of the
              two it is costs one sentence and stops the reader concluding the wrong one.
            */}
            {clientOwnerName ?? "Namnet gick inte att slå upp."}
          </Fact>

          <Fact term="Återställning" testId="account-recovery">
            {recoveryOwnerAbsent ? (
              <Badge tone="warn" plain>
                Ingen utpekad
              </Badge>
            ) : (
              (recoveryOwnerName ?? "Namnet gick inte att slå upp.")
            )}
          </Fact>

          <Fact term="Tvåfaktor" testId="account-mfa">
            {mfa ? (
              mfa.warn ? (
                <Badge tone="warn" plain>
                  {mfa.label}
                </Badge>
              ) : (
                <Quiet>{mfa.label}</Quiet>
              )
            ) : (
              <Raw value={reference.mfa_status} />
            )}
          </Fact>

          {reference.service_url ? (
            <Fact term="Adress" testId="account-url">
              {/*
                Rendered as text rather than as a link. It is a non-secret administration address
                somebody typed into the register by hand, and a portal that turns typed-in text into a
                clickable destination is a portal that will one day send a client somewhere it was
                never checked.
              */}
              <span className="k-mono">{reference.service_url}</span>
            </Fact>
          ) : null}

          {reference.non_secret_identifier ? (
            <Fact term="Kontonummer hos leverantören" testId="account-identifier">
              <span className="k-mono">{reference.non_secret_identifier}</span>
            </Fact>
          ) : null}
        </dl>

        {/*
          The one note the client may read. schema.md section 7: admin-only operational notes are
          audit events or offboarding-task notes, not a client-visible column hidden by the interface,
          so there is nothing being withheld here to mark as withheld.
        */}
        {reference.client_visible_note ? (
          <p className="k-account-note" data-testid="account-note">
            {reference.client_visible_note}
          </p>
        ) : null}
      </div>

      <div className="k-subhead">
        <h3>Behörigheter</h3>
      </div>

      {access === null ? (
        /*
          Not an empty list. The `jsonb` was not an array, which no version of the view produces, so
          this build cannot read the answer it received. "Nobody has access" and "this cannot be
          read" are opposite claims and only one of them is true here.
        */
        <p className="k-line" data-testid="access-unreadable">
          Behörigheterna för det här kontot gick inte att läsa. Hör av er i chatten, så tittar vi på det.
        </p>
      ) : access.length === 0 ? (
        <p className="k-line" data-testid="access-empty">
          Ingen behörighet hos er, hos en efterträdare eller hos en annan part är uppskriven. Vår egen står
          inte med.
        </p>
      ) : (
        <ul className="k-access-list" aria-label={`Behörigheter för ${reference.account_label}`}>
          {access.map((record) => (
            <AccessRow key={record.id} record={record} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function AccountsRoute() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const accounts = useClientAccountMap(tenantId);
  const contacts = useTenantContacts(tenantId);
  const services = useServices(tenantId);

  /**
   * The head and the standing line are rendered in every state this screen has, including the
   * failing ones: the line that frames what the page leaves out has to survive into the states where
   * the page has nothing to show, because those are the states a reader is most likely to misread.
   */
  const page = (content: ReactNode) => (
    <div className="k-page">
      <ClientHead
        title="Konton och behörigheter"
        after={
          <Hint>Listan svarar på en enda fråga. Om vi försvann i morgon, skulle ni kunna fortsätta?</Hint>
        }
      />
      {/*
        The standing line the empty access lists repeat. It names the exclusion without naming a
        single row, because a per-account marker would say which accounts Petrovic can reach, and that
        is the inference schema.md section 7's two locks exist to refuse.
      */}
      <div className="callout" data-testid="petrovic-access-excluded">
        <ShieldCheck size={18} aria-hidden="true" />
        <div>Vår egen behörighet står inte med. En tom lista betyder inte att ingen kommer in.</div>
      </div>
      {content}
    </div>
  );

  const stateBox = (state: ReactNode) => <section className="panel">{state}</section>;

  if (identity.isLoading || accounts.isLoading || contacts.isLoading || services.isLoading) {
    return page(stateBox(<BoxState kind="loading" title="Hämtar era konton" />));
  }

  /*
    Before the tenant check, on purpose. `usePortalIdentity` answers with no tenant when its own
    read failed, and the refusal below reads that as "this account belongs to no customer". Showing
    a denial for a network fault is the defect the identity hook's own comment lists.
  */
  if (identity.isError) {
    return page(
      stateBox(
        <BoxState
          kind="error"
          title="Kontolistan gick inte att hämta"
          body="Vi kunde inte läsa vem du är. Försök igen, och hör av dig i chatten om det upprepas."
          onRetry={() => void identity.refetch()}
        />
      )
    );
  }

  if (!tenantId) {
    return page(
      stateBox(
        <BoxState kind="denied" title="Den här vyn är inte din" body="Kontot hör inte till någon kund i portalen." />
      )
    );
  }

  /*
    All three reads fail together, and the names are part of the failure rather than something to
    render as missing. A failed contacts read would otherwise put "Namnet gick inte att slå upp" on
    every owner row at once, which reads as a register nobody has filled in rather than as a page
    that could not load.
  */
  if (accounts.isError || contacts.isError || services.isError) {
    return page(
      stateBox(
        <BoxState
          kind="error"
          title="Kontolistan gick inte att hämta"
          body="Sidan gick inte att hämta just nu. Försök igen."
          onRetry={() => {
            void accounts.refetch();
            void contacts.refetch();
            void services.refetch();
          }}
        />
      )
    );
  }

  const entries = composeAccountMap(accounts.data ?? [], contacts.data ?? [], services.data ?? []);

  /*
    An empty register is an absence and never an all-clear. Nothing has been written down, so the
    page cannot report that the accounts are in order; it can only report that nobody has recorded
    them. The sentence says which, and it points at the one place a client can do something about
    it.
  */
  if (entries.length === 0) {
    return page(
      stateBox(
        <BoxState
          kind="empty"
          title="Inget konto är uppskrivet än"
          body="Vi har inte skrivit upp något leverantörskonto för er ännu, så den här sidan säger ingenting om hur era konton står. Hör av er i chatten om ni vill att vi går igenom dem."
        />
      )
    );
  }

  return page(
    <section className="stack" aria-label="Era konton">
      {entries.map((entry) => (
        <AccountBox key={entry.reference.id} entry={entry} />
      ))}
    </section>
  );
}
